/**
 * appointmentReminders — lembretes locais (notificações no dispositivo) para os
 * compromissos da Agenda.
 *
 * TODA a dependência nativa (@capacitor/local-notifications) fica encapsulada
 * aqui. No web tudo é no-op — o plugin só roda em nativo.
 *
 * Estratégia de RECONCILIAÇÃO (robusta ao bug de id offline_→UUID, ver
 * [[orcamigo-offline-sync-uuid-bug]]): nunca agendamos ad-hoc por mutação. Em
 * vez disso `reconcileAppointmentReminders(appointments)` cancela TODAS as
 * notificações locais do app e reagenda, para cada compromisso FUTURO com
 * `reminder_minutes` e `status='scheduled'`, uma notificação em
 * `starts_at - reminder_minutes`. O id numérico da notificação é derivado de
 * forma DETERMINÍSTICA do id ATUAL do compromisso, então um id offline_ que
 * vira UUID real é reconciliado sozinho no próximo reagendamento.
 *
 * Chame `reconcileAppointmentReminders` quando a query de compromissos assentar
 * (na AgendaPage) e após o sync concluir (evento `appointments-updated`).
 *
 * (Futuro, fora do MVP) lembrete resiliente cross-device/uninstall via OneSignal
 * + pg_cron — o app já tem ambos; deixar documentado, não implementar agora.
 */
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

/** Hora local padrão para ancorar lembretes de compromissos de dia inteiro. */
export const ALL_DAY_REMINDER_HOUR = 8;

export interface ReminderAppointment {
  id: string;
  starts_at?: string | null;
  all_day?: boolean | null;
  reminder_minutes?: number | null;
  status?: string | null;
  title?: string | null;
  location?: string | null;
}

/**
 * Deriva um id numérico ESTÁVEL (int32 positivo) de um id de compromisso
 * (uuid OU offline_...). Ids de notificação do LocalNotifications precisam ser
 * inteiros de 32 bits — hash djb2 mascarado para [1, 2^31-1]. Determinístico:
 * o mesmo id sempre gera o mesmo número, então cancelar+reagendar reconcilia.
 */
export function stableNotificationId(id: string): number {
  let h = 5381;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    // h * 33 ^ c, forçando aritmética de int32 a cada passo.
    h = (((h << 5) + h) ^ str.charCodeAt(i)) | 0;
  }
  // Mapeia para [1, 2147483646], evitando 0 (id inválido em alguns SOs).
  return (Math.abs(h) % 2147483646) + 1;
}

/**
 * Calcula o instante do lembrete de um compromisso, ou null quando não há
 * lembrete a agendar. Puro (testável). Para compromissos de dia inteiro, ancora
 * em `allDayHour` (08:00 por padrão) do dia local; para os com hora, usa
 * `starts_at`. Subtrai `reminder_minutes`.
 */
export function reminderFireDate(
  appointment: ReminderAppointment,
  allDayHour: number = ALL_DAY_REMINDER_HOUR,
): Date | null {
  if (appointment.reminder_minutes == null) return null;
  if (!appointment.starts_at) return null;

  const base = new Date(appointment.starts_at);
  if (Number.isNaN(base.getTime())) return null;

  // Compromissos de dia inteiro guardam starts_at na meia-noite LOCAL do dia
  // (ver AppointmentFormModal), então getFullYear/Month/Date dão o dia correto.
  const anchor = appointment.all_day
    ? new Date(base.getFullYear(), base.getMonth(), base.getDate(), allDayHour, 0, 0, 0)
    : base;

  return new Date(anchor.getTime() - appointment.reminder_minutes * 60_000);
}

function reminderTitle(a: ReminderAppointment): string {
  const t = (a.title ?? '').trim();
  return t || 'Compromisso';
}

function reminderBody(a: ReminderAppointment): string {
  const when = a.starts_at ? new Date(a.starts_at) : null;
  const loc = (a.location ?? '').trim();
  const hasWhen = when && !Number.isNaN(when.getTime());

  if (a.all_day) {
    // A "1 dia antes" reminder fires the previous day, so don't claim "hoje" —
    // state the actual date instead.
    if (hasWhen) {
      const dd = String(when!.getDate()).padStart(2, '0');
      const mm = String(when!.getMonth() + 1).padStart(2, '0');
      return loc ? `Compromisso em ${dd}/${mm} · ${loc}` : `Compromisso em ${dd}/${mm}.`;
    }
    return loc ? `Compromisso · ${loc}` : 'Você tem um compromisso.';
  }

  if (!hasWhen) {
    return loc ? `Local: ${loc}` : 'Você tem um compromisso.';
  }
  const hh = String(when!.getHours()).padStart(2, '0');
  const mm = String(when!.getMinutes()).padStart(2, '0');
  return loc ? `Às ${hh}:${mm} · ${loc}` : `Você tem um compromisso às ${hh}:${mm}.`;
}

/**
 * Garante permissão de notificação local. No web (ou fora de nativo) retorna
 * false sem prompt. Pede na primeira vez; se já concedida, não repete.
 */
export async function ensureReminderPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const check = await LocalNotifications.checkPermissions();
    if (check.display === 'granted') return true;
    const req = await LocalNotifications.requestPermissions();
    return req.display === 'granted';
  } catch (e) {
    console.warn('[appointmentReminders] permission check failed:', e);
    return false;
  }
}

/**
 * Cancela todas as notificações locais do app e reagenda os lembretes de todos
 * os compromissos futuros elegíveis. No web é no-op. Se a permissão for negada,
 * apenas não notifica (o compromisso já foi salvo normalmente).
 *
 * Idempotente: pode ser chamada sempre que a lista de compromissos mudar.
 */
export async function reconcileAppointmentReminders(
  appointments: ReminderAppointment[],
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const granted = await ensureReminderPermission();
    if (!granted) return;

    // Cancela TODAS as notificações locais pendentes (todas são nossas — o app
    // só usa LocalNotifications para lembretes de compromisso).
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({
        notifications: pending.notifications.map((n) => ({ id: n.id })),
      });
    }

    const now = Date.now();
    const seenIds = new Set<number>();
    const notifications = [] as Array<{
      id: number;
      title: string;
      body: string;
      schedule: { at: Date; allowWhileIdle: boolean };
    }>;

    for (const a of appointments ?? []) {
      if (!a || a.status !== 'scheduled') continue;
      const fireAt = reminderFireDate(a);
      if (!fireAt) continue;
      // Só lembretes futuros — o passado não dispara e polui o histórico.
      if (fireAt.getTime() <= now) continue;

      const notifId = stableNotificationId(String(a.id));
      // Guarda contra colisão de hash rara dentro do mesmo lote (dropar 1 é
      // melhor do que o schedule inteiro falhar por id duplicado).
      if (seenIds.has(notifId)) continue;
      seenIds.add(notifId);

      notifications.push({
        id: notifId,
        title: reminderTitle(a),
        body: reminderBody(a),
        schedule: { at: fireAt, allowWhileIdle: true },
      });
    }

    if (notifications.length > 0) {
      await LocalNotifications.schedule({ notifications });
    }
  } catch (e) {
    console.warn('[appointmentReminders] reconcile failed:', e);
  }
}
